import React, { useState, useEffect } from 'react';
import { useLanguage } from '../utils/LanguageContext';
import { uploadImage, getImages } from '../utils/api';
import SendPicModal from './SendPicModal';

interface Picture {
    url: string;
    name: string;
    comment?: string;
}

const SendPic: React.FC = () => {
    const { t } = useLanguage();
    const [name, setName] = useState('');
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [comment, setComment] = useState('');
    const [pictures, setPictures] = useState<Picture[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedPicture, setSelectedPicture] = useState<Picture | null>(null);

    useEffect(() => {
        // Load existing pictures when component mounts
        const loadPictures = async () => {
            try {
                const loadedPictures = await getImages();
                setPictures(loadedPictures);
            } catch (err) {
                setError(t('gallery.error.loading'));
            }
        };
        loadPictures();
    }, [t]);

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            setSelectedFile(e.target.files[0]);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedFile) return;

        const formData = new FormData();
        formData.append('image', selectedFile);
        formData.append('name', name);
        formData.append('comment', comment);

        try {
            await uploadImage(formData);
            const updatedPictures = await getImages();
            setPictures(updatedPictures);
            
            // Reset form
            setName('');
            setSelectedFile(null);
            setComment('');
            setError(null);
            
            // Reset file input
            const fileInput = document.getElementById('image') as HTMLInputElement;
            if (fileInput) fileInput.value = '';
        } catch (err) {
            setError(t('gallery.error.upload'));
        }
    };

    const openModal = (picture: Picture) => {
        setSelectedPicture(picture);
        setIsModalOpen(true);
    };

    return (
        <div className="gallery-section">
            <div className="upload-section">
                <form onSubmit={handleSubmit}>
                    <h2>{t('gallery.title')}</h2>
                    {error && <div className="error-message">{error}</div>}
                    <div className="form-group">
                        <label htmlFor="name">{t('gallery.name')}</label>
                        <input
                            type="text"
                            id="name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="input-field"
                            required
                        />
                    </div>
                    <div className="form-group">
                        <label htmlFor="image">{t('gallery.select')}</label>
                        <input
                            type="file"
                            id="image"
                            accept="image/*"
                            onChange={handleImageChange}
                            className="input-field"
                            required
                        />
                    </div>
                    <div className="form-group">
                        <label htmlFor="comment">{t('gallery.comment')}</label>
                        <textarea
                            id="comment"
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            className="input-field"
                            rows={3}
                        />
                    </div>
                    <button type="submit" className="button" disabled={!selectedFile}>
                        {t('gallery.submit')}
                    </button>
                </form>
            </div>

            <div className="image-gallery">
                {pictures.map((picture, index) => (
                    <div key={index} className="gallery-item" onClick={() => openModal(picture)}>
                        <img src={picture.url} alt={picture.name} loading="lazy" />
                        <div className="image-overlay">
                            <p>{picture.name}</p>
                            {picture.comment && <p className="image-comment">{picture.comment}</p>}
                        </div>
                    </div>
                ))}
            </div>

            {isModalOpen && selectedPicture && (
                <SendPicModal
                    picture={selectedPicture}
                    onClose={() => setIsModalOpen(false)}
                />
            )}
        </div>
    );
};

export default SendPic;